import React from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    Typography,
    Button,
    Box,
    IconButton,
    Chip,
    styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const FREE_TOOLS = [
    { label: "Options", route: "/options-table" },
    { label: "Greeks", route: "/greek-chart" },
    { label: "IV", route: "/bar-chart/NIFTY_Strike_IV" },
    { label: "OI", route: "/line-chart/NF_Options_OI_Change_Charts" },
    { label: "Volume", route: "/line-chart/NF_Options_Vol_Charts" },
    { label: "Participant positioning", route: "/position-table" },
    { label: "Indices", route: "/index-table" },
];

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
        backgroundColor: "rgb(13, 32, 63)",
        color: "#ffffff",
        borderRadius: "12px",
        maxWidth: "650px",
        width: "100%",
        padding: theme.spacing(2),
        position: "relative",
    },
}));

const StyledButton = styled(Button)(({ variant }) => ({
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    padding: "6px 16px",
    borderRadius: "6px",
    ...(variant === "outlined"
        ? {
              borderColor: "rgba(255, 255, 255, 0.4)",
              color: "#ffffff",
              "&:hover": {
                  borderColor: "#ffffff",
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
          }
        : {
              backgroundColor: "#ffffff",
              color: "rgb(13, 32, 63)",
              "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
          }),
}));

const FeatureChip = styled(Chip)({
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    color: "#ffffff",
    borderColor: "rgba(255, 255, 255, 0.2)",
    fontWeight: 500,
    fontSize: "0.85rem",
    padding: "4px 8px",
    cursor: "pointer",
    "&:hover": {
        backgroundColor: "rgba(63, 105, 248, 0.22)",
        borderColor: "rgba(122, 156, 255, 0.55)",
    },
});

export default function FreeToolsModal({ open, onClose, onExplore }) {
    const navigate = useNavigate();

    const handleToolNavigate = (route) => {
        navigate(route);
        onClose();
    };

    const handleExplore = () => {
        if (onExplore) {
            onExplore();
        } else {
            navigate(FREE_TOOLS[0].route);
        }
        onClose();
    };

    return (
        <StyledDialog
            open={open}
            onClose={onClose} // Handles Escape key and backdrop click
        >
            {/* Top Right 'X' Close Button */}
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 12,
                    top: 12,
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                        color: "#ffffff",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 2 }}>
                {/* Title */}
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 700, mb: 2, pr: 4 }}
                >
                    There's a lot more in your Free account
                </Typography>

                {/* Subtitle */}
                <Typography
                    variant="body1"
                    sx={{ mb: 2, color: "rgba(255, 255, 255, 0.9)" }}
                >
                    Along with Market Profile, your Free access includes tools
                    for:
                </Typography>

                {/* Tools Chips / Badge Grid */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 3,
                        my: 2,
                    }}
                >
                    {FREE_TOOLS.map((tool) => (
                        <FeatureChip
                            key={tool.label}
                            label={tool.label}
                            variant="outlined"
                            clickable
                            onClick={() => handleToolNavigate(tool.route)}
                        />
                    ))}
                </Box>

                {/* Footer Note */}
                <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "rgba(255, 255, 255, 0.8)" }}
                >
                    All of it stays free, on your account.
                </Typography>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <StyledButton
                        variant="contained"
                        onClick={handleExplore}
                    >
                        Explore Free tools
                    </StyledButton>
                    <StyledButton variant="outlined" onClick={onClose}>
                        Later
                    </StyledButton>
                </Box>
            </DialogContent>
        </StyledDialog>
    );
}
